import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PushPinIcon from '@mui/icons-material/PushPin';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { makeStyles } from 'tss-react/mui';
import { useCreateNote } from '../../../sic-lib/api/hooks/useNotes';
import { ContactNoteStatus } from '../../../sic-lib/enums/ContactNoteStatus';
import ButtonLoading from '../../Shared/ButtonLoading';

interface Props {
    transcriptSummary: { [x: string]: string | string[] }[];
    contactId: number | undefined;
}

export default function AddCallAnalysisToNote({ transcriptSummary, contactId = -1 }: Props) {
    const { classes, cx } = useStyles();
    const [noteStatus, setNoteStatus] = React.useState<ContactNoteStatus>(ContactNoteStatus.Active);
    const [isCopied, setIsCopied] = React.useState<boolean>(false);

    const { mutate: createNote, isError, isLoading, isSuccess } = useCreateNote(contactId);

    let noteText: string = React.useMemo(() => {
        let mappedTranscriptSummary: string = '';
        transcriptSummary.forEach(itm => {
            mappedTranscriptSummary = mappedTranscriptSummary.concat(
                `${Object.keys(itm)}: ${Object.values(itm)}\n`,
            );
        });
        return mappedTranscriptSummary;
    }, [transcriptSummary]);

    const handleCopySummaryToNote = () => {
        if (contactId !== -1) {
            createNote({
                contactId: contactId,
                text: noteText,
                noteStatus: noteStatus,
            });
        }
    };

    const handlePin = () => {
        setNoteStatus(
            noteStatus === ContactNoteStatus.Active
                ? ContactNoteStatus.Pinned
                : ContactNoteStatus.Active,
        );
    };

    const copyHandler = () => {
        setIsCopied(true);
        setTimeout(() => {
            setIsCopied(false);
        }, 3000);
    };

    return (
        <Stack direction="row" spacing={2} alignSelf="flex-end" mt="auto">
            <ButtonLoading
                variant="contained"
                onClick={handleCopySummaryToNote}
                isLoading={isLoading}
                isSuccess={isSuccess}
                successLabel="Successfully added to notes"
                isError={isError}
                errorLabel="An Error Occurred"
                className={cx(
                    classes.btnAddNote,
                    noteStatus === ContactNoteStatus.Pinned && classes.notePinned,
                )}
            >
                Copy Summary to Note
            </ButtonLoading>
            <Tooltip
                title={noteStatus === ContactNoteStatus.Pinned ? 'Unpin' : 'Pin'}
                placement="top"
            >
                <IconButton aria-label="Pause" size="small" onClick={handlePin}>
                    <PushPinIcon />
                </IconButton>
            </Tooltip>
            <CopyToClipboard text={noteText || ''} onCopy={copyHandler}>
                <Tooltip title={isCopied ? 'Copied' : 'Copy Summary To Clipboard'} placement="top">
                    <IconButton aria-label="Copy" size="small">
                        <ContentCopyIcon />
                    </IconButton>
                </Tooltip>
            </CopyToClipboard>
        </Stack>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    btnAddNote: {
        backgroundColor: theme.palette.noteStatus.main,
        color: theme.palette.btnDefault.main,
        marginRight: theme.spacing(2),
        '&:hover': {
            backgroundColor: theme.palette.noteStatus.main,
        },
    },
    notePinned: {
        backgroundColor: theme.palette.noteStatus.light,
        color: theme.palette.appHeader.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.noteStatus.light,
        },
    },
}));
